// Componente ProtectedRoute - Protege rutas que requieren autenticación
// Redirige a login si el usuario no está autenticado

import { useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { usePermissions } from '../hooks/usePermissions'
import { FIXED_ROLES, ACTIONS } from '../constants/permissions'
import { logAuditEvent } from '../services/auditLogs.service'

/**
 * Componente ProtectedRoute
 * Protege una ruta requiriendo autenticación y permisos
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Componente a renderizar si está autenticado
 * @param {Array} props.allowedRoles - Roles permitidos (opcional)
 * @param {string} props.requiredModule - Módulo requerido (opcional)
 * @param {string} props.requiredAction - Acción requerida (opcional)
 */
function ProtectedRoute({ 
  children, 
  allowedRoles = [],
  requiredModule = null,
  requiredAction = ACTIONS.VIEW
}) {
  const { currentUser, loading } = useAuth()
  const { can, hasAdminAccess, getNormalizedRole } = usePermissions()
  const location = useLocation()
  const loggedRef = useRef({})

  const isAdminRoute = location.pathname.startsWith('/admin')
  const userRole = getNormalizedRole() || FIXED_ROLES.CLIENT
  const roleDenied = allowedRoles.length > 0 && userRole !== FIXED_ROLES.SUPERADMIN && !allowedRoles.includes(userRole)
  const permissionDenied = requiredModule ? !can(requiredModule, requiredAction) : false
  const adminDenied = isAdminRoute && !hasAdminAccess()
  const denyReason = !currentUser
    ? 'not_authenticated'
    : adminDenied
      ? 'admin_access_denied'
      : roleDenied
        ? 'role_denied'
        : permissionDenied
          ? 'permission_denied'
          : null

  useEffect(() => {
    if (loading || !denyReason) return
    const key = `${denyReason}:${location.pathname}`
    if (loggedRef.current[key]) return
    loggedRef.current[key] = true
    logAuditEvent({
      level: 'warning',
      category: 'access',
      action: denyReason,
      message: `Acceso denegado a ${location.pathname}`,
      metadata: {
        requiredModule,
        requiredAction,
        allowedRoles,
        isAdminRoute
      }
    })
  }, [denyReason, loading, location.pathname, requiredModule, requiredAction, allowedRoles, isAdminRoute])

  // Mostrar loading mientras se verifica la autenticación
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <p>Cargando...</p>
      </div>
    )
  }

  // Si no hay usuario, redirigir a login
  if (!currentUser) {
    return <Navigate to="/login" replace />
  }

  if (adminDenied) {
    return <Navigate to="/" replace />
  }

  if (allowedRoles.length > 0) {
    if (userRole === FIXED_ROLES.SUPERADMIN) {
      return children
    }
    if (roleDenied) {
      return <Navigate to="/" replace />
    }
  }

  if (permissionDenied) {
    return <Navigate to="/" replace />
  }

  // Usuario autenticado y con permisos válidos, renderizar el componente
  return children
}

export default ProtectedRoute

